import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { cloneDeep, isEmpty, isNil } from 'lodash';
import moment from 'moment';
import { exportImage,Image, geometry } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { getTimeZoneAbbr } from '../../../../helpers/timezones';

import BobLoadingMask from '../../../../components/BobLoadingMask';

import {
    Chart,
    ChartLegend,
    ChartSeries,
    ChartSeriesItem,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartTooltip,
    ChartTitle,
    exportVisual,
    ChartPanes,
    ChartPane
} from '@progress/kendo-react-charts';

import classes from './ScoreGraph.module.css';

const SensorGraph = ({
    allSensors,
    bobLoading,
    dashboardEndTimeStamp,
    entityScoringData,
    isActive,
    rawUnits,
    selectedRuleSensors,
    selectedEquipment,
    selectedEquipmentRule,
    ...props
}) => {
    const [scores, setScores] = useState([]);
    const [scoreChartHeight, setScoreChartHeight] = useState('60vh');
    const chart = React.createRef();
    const [ganttDs, setGanttDs] = useState([]);
    const [ganttAxisMax, setGanttAxisMax] = useState(0);
    const [ganttAxisMin, setGanttAxisMin] = useState(0);
    const [booleanDataSet, setBooleanDataSet] = useState([]);
    const { Rect, Point, Size } = geometry;
    const minWidth2560 = matchMedia('(min-width: 2560px');
    const zoom125 = matchMedia('(min-resolution: 1.25dppx');

    const updateScoreChart2560 = (e) => {
        if (e.matches) {
            setScoreChartHeight('71vh');
        }
        else {
            setScoreChartHeight('60vh');
        }

    };

    const mqlZoom125 = (e) => {
        if (e.matches) {
            setScoreChartHeight('49vh');
        }
        else {
            setScoreChartHeight('60vh');
        }
    };

    useEffect(() => {
        return () => {
            minWidth2560.removeEventListener('change', updateScoreChart2560);
            zoom125.removeEventListener('change', mqlZoom125);
        };
    }, []);

    useEffect(() => {
        if (window.devicePixelRatio === 1) {
            setScoreChartHeight('60vh');
        }
        else {
            setScoreChartHeight('49vh');
        }

        minWidth2560.addEventListener('change', updateScoreChart2560);
        zoom125.addEventListener('change', mqlZoom125);

        const mediaQuery = window.matchMedia('(min-width: 2560px)');
        if (mediaQuery.matches) {
            updateScoreChart2560(mediaQuery);
        }

    }, []);

    useEffect(() => {
        let data = [];
        let entityScoringDataCopy = cloneDeep(entityScoringData);
        let numericData = [], booleans =[];
        if (!isEmpty(entityScoringDataCopy)) {
            Object.keys(entityScoringDataCopy[0]).forEach(item => {
                let id = item.split('_');
                id = id[1];

                if (
                    (item.includes('Name') && !item.includes('sensorParentName') && !item.includes('ruleName'))
                    && (item.includes('sensor') || item.includes('FvoSensor'))) {
                    Object.keys(entityScoringDataCopy[0]).forEach(point => {
                        const presentationUnitId = entityScoringDataCopy[0][`sensorPresentationUnitId_${ id }`];
                        if (point === `sensorVal_${id}` || point === `ruleFvoSensorVal_${id}`) {
                            let obj = {
                                name:      entityScoringDataCopy[0][item],
                                key:       point,
                                axis:      'Left Y Axis',
                                data:      [],
                                unit:      presentationUnitId,
                                isWeather: false
                            };

                            if (point.includes('temp')) {
                                obj.unit = '°F';
                                obj.isWeather = true;
                            } else if (point.includes('humidity')) {
                                obj.unit = '%';
                                obj.isWeather = true;
                            } else if (point.includes('pressure')) {
                                obj.unit = ' hPa';
                                obj.isWeather = true;
                            }
                            data.push(obj);
                        }
                    });
                }
            });

            data.forEach(item => {
                entityScoringDataCopy.forEach(score => {
                    let entry = {
                        timestamp: new Date(score['start']),
                        value:     !isNil(score[item.key]) ? renderValue(score, item) : null,
                        unit:      !isNil(score[item.key]) ? renderUnit(score, item) : null
                    };
                    item.data.push(entry);
                });
                if (item.data.length >0 && typeof(item.data[0].value) === 'boolean' ) {
                    booleans.push(item);
                }
                else numericData.push(item);
            });
        }

        setScores(numericData);
        processGanttDataset(booleans);
    }, [entityScoringData]);

    const getGanttSeries = (c, i) => {
        return {
            sensorId:   c.sensorId,
            sensorName: c.sensorName,
            axis:       'yAxis',
            dataColor:  i % 2 === 0 ? '#ff6d01' : '#6f6f6f',
            data:       []
        };
    };

    const labelContent = (e) => {
        if (e.value !== 0 && e.value - 1 < booleanDataSet.length) {
            return booleanDataSet[e.value - 1].name;
        }
        return '';
    };

    const processGanttDataset = (booleanDataSet) =>{
        let ganttDs = [];
        let ganttAxisMin = 0;
        let ganttAxisMax = 0;
        ganttAxisMax = booleanDataSet.length + 1;
        //Creating the gaps for plotting Gantt chart in bottom pane for boolean dataset
        booleanDataSet.forEach((bdata, i) => {
            bdata.data = bdata.data.sort(function (a, b) {
                return a.timestamp - b.timestamp;
            });

            let ganttSeries = getGanttSeries(bdata, i);
            ganttSeries.sensorId = bdata.key;
            ganttSeries.sensorName = bdata.name;
            let lastval = bdata.data[0].timestamp.getTime();
            bdata.data.forEach((data, j) => {
                if (lastval !== data.timestamp.getTime()) {
                    if (ganttSeries.data.length >0 && ganttSeries.data[ganttSeries.data.length - 1].booleanValue !== data.value) {
                        ganttDs.push(ganttSeries);
                        let lastpoint = {...ganttSeries.data[ganttSeries.data.length - 1]};
                        lastpoint.booleanValue = data.value;
                        ganttSeries = getGanttSeries(bdata, i);
                        ganttSeries.sensorId = bdata.key;
                        ganttSeries.sensorName = bdata.name;
                        ganttSeries.data.push(lastpoint);
                    }
                    data.booleanValue = data.value;

                    if (isNil(data.value)) {
                        ganttSeries.dataColor = 'url(#pattern2)';
                    }
                    else if (data.value.toString().toLowerCase() === 'false') {
                        ganttSeries.dataColor = '#FFFFFF';
                    }
                    data.value = i + 1;
                    ganttSeries.data.push(data);
                    lastval = lastval + (data.sampleInterval * 60 * 1000);
                }

            });
            if (ganttSeries.data.length > 0) {
                if (ganttSeries.data.length === 1 && !ganttSeries.data[0].booleanValue) {
                    ganttSeries.data = [];
                }
                ganttDs.push(ganttSeries);
            }
        });
        setGanttDs(ganttDs);
        setGanttAxisMin(ganttAxisMin);
        setBooleanDataSet(booleanDataSet);
        setGanttAxisMax(ganttAxisMax);
    };


    const getUnitDisplayValue = (rawUnitId) => {
        let targetUnit;
        if (rawUnits) {
            rawUnits.forEach(unit => {
                unit.items.forEach(item => {
                    if (item.id === rawUnitId) {
                        targetUnit = { ...item };
                    }
                });
            });
        }
        return targetUnit ? targetUnit.displayValue : '-';
    };

    const renderUnit = (score, item) => {
        if (item.key.includes('sensor')) {
            let id = item.key.split('_');
            if (!id) return;
            const selectedSensor = allSensors.find(sensor => sensor.id === id[1]);

            if (selectedSensor) return selectedSensor.presentationUnitId;
            return null;
        }
    };

    const renderValue = (score, item) => {
        if (score[item.key] === 'true' || score[item.key] === 'True') {
            return true;
        } else if (score[item.key] === 'false' || score[item.key] === 'False') {
            return false;
        } else {
            if (item.key.toLowerCase().includes('sensor')) {
                return score[item.key];
            } else {
                return score[item.key] * 100;
            }
        }
    };

    const downloadChart = () =>{
        if (chart) {
            let chartRefValue = chart.current;
            if (chartRefValue && chartRefValue.element) {
                const imageRect = new Rect(new Point(chartRefValue.element.offsetWidth-110, chartRefValue.element.offsetHeight), new Size(110, 30));
                const imageUrl = `/assets/images/k2acopyright.png`;
                const image = new Image(imageUrl, imageRect);
                chartRefValue.surface.draw(image);
                const chartVisual = exportVisual(chartRefValue);
                if (chartVisual) {
                    exportImage(chartVisual).then(dataURI => saveAs(dataURI, 'Score graph'));
                }
            }
        }
    };

    props.propsFunc.downloadChart = downloadChart;

    const SharedTooltip = (props) => {
        const zone = moment.tz.guess();
        const pointDate = props.points[0].category;
        let points = props.points.filter(c => c.series.axis !== 'bottom');
        let booleans = props.points.filter(c => c.series.axis === 'bottom');

        let sensors = [];
        if (ganttDs && ganttDs.length > 0) {
            for (var i = 0; i < ganttDs.length; i++) {
                let found = false;
                for (var j = 0; j < booleans.length; j++) {
                    if (ganttDs[i].sensorName === booleans[j].series.name) {
                        found = true;
                        if (sensors.indexOf(ganttDs[i].sensorName) === -1) {
                            sensors.push(ganttDs[i].sensorName);
                            points.push({ dataItem: { booleanValue: booleans[j].series.color === 'url(#pattern2)' ? null: booleans[j].series.color ===  '#FFFFFF' ? false:true }, series: { color: (booleans[j].series.color === '#FFFFFF' || booleans[j].series.color === 'url(#pattern2)') ? '#23c8fa':booleans[j].series.color, axis: 'Boolean', name: ganttDs[i].sensorName } });
                        }
                        break;
                    }
                }
                if (!found && (sensors.indexOf(ganttDs[i].sensorName) === -1)) {
                    sensors.push(ganttDs[i].sensorName);
                    points.push({ dataItem: { booleanValue: false }, series: { color: ganttDs[i].dataColor, axis: 'Boolean', name: ganttDs[i].sensorName } });
                }
            }
        }

        let group = points.reduce((r, a) => {
            if (!a.series.axis) {
                a.series.axis = 'Axis';
            }
            r[a.series.axis] = [...r[a.series.axis] || [], a];
            return r;
        }, {});

        let finalArray = [];
        let booleanobj = null;
        for (var pt in group) {
            if (Object.prototype.hasOwnProperty.call(group, pt) && pt !== 'Boolean') {
                finalArray.push({ axis: pt, points: group[pt].sort((a, b) => { return a.series.name.localeCompare(b.series.name); }) });
            }
            else if (pt === 'Boolean') {
                booleanobj = { axis: pt, points: group[pt].sort((a, b) => { return a.series.name.localeCompare(b.series.name); }) };
            }
        }
        if (booleanobj) {
            finalArray.push(booleanobj);
        }

        const booleanAdjustment = booleanobj ? 30 : 0;
        let marginTop = -40;
        switch (points.length) {
            case 1:
                marginTop = 300 - booleanAdjustment;
                break;
            case 2:
                marginTop = 275 - booleanAdjustment;
                break;
            case 3:
                marginTop = 260 - booleanAdjustment;
                break;
            case 4:
                marginTop = 245 - booleanAdjustment;
                break;
            case 5:
                marginTop = 230 - booleanAdjustment;
                break;
            case 6:
                marginTop = 215 - booleanAdjustment;
                break;
            case 7:
                marginTop = 190 - booleanAdjustment;
                break;
            case 8:
                marginTop = 175 - booleanAdjustment;
                break;
            case 9:
                marginTop = 150 - booleanAdjustment;
                break;
            case 10:
                marginTop = 130 - booleanAdjustment;
                break;
            case 11:
                marginTop = 110 - booleanAdjustment;
                break;
            default:
                marginTop = 300 - booleanAdjustment;
        }
        let root = document.documentElement;
        root.style.setProperty('--sharedTooltipTop', marginTop + 'px');

        const renderItemValue = point => {
            let value = point.dataItem.value;
            if (value === true) {
                return 'True';
            } else if (value === false) {
                return 'False';
            } else if (value === null) {
                return 'Null';
            } else if (point.series.isWeather) {
                return parseFloat(value).toFixed(2) + point.series.unit;
            } else {
                value = value.toString().includes('.') ? parseFloat(value).toFixed(2) : value;
                return value + ' ' + getUnitDisplayValue(point.series.unit);
            }
        };

        return (
            <div>
                <React.Fragment>
                    <div style={{color: '#000000', fontWeight: 'bold', height: 20, marginBottom: 8}}>
                        {
                            points && points.length > 0 ?
                                moment(pointDate).format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(moment(pointDate), zone)
                                : ''
                        }
                    </div>
                    {
                        finalArray.map((c, index) => (
                            <React.Fragment key={index}>
                                <React.Fragment>
                                    {
                                        c.points.map((point) => (
                                            <div key={Math.random()} style={{overflowWrap: 'break-word', height: 20, color: point.series && point.series.color ? point.series.color : '#000000'}}>
                                                {
                                                    point.series.axis === 'Boolean' ?
                                                        <React.Fragment>
                                                            {
                                                                point.series.name ?
                                                                    <React.Fragment>{`${ point.series.name } - ${ point.dataItem.booleanValue === true ?
                                                                        'True' : point.dataItem.booleanValue === null ? 'No Data' : 'False' }`
                                                                    }
                                                                    </React.Fragment>
                                                                    : null
                                                            }
                                                        </React.Fragment>
                                                        : <React.Fragment>
                                                            <React.Fragment>
                                                                {
                                                                    point.series.name ?
                                                                        <React.Fragment>
                                                                            {`${ point.series.name } - `}
                                                                        </React.Fragment>
                                                                        : null
                                                                }
                                                            </React.Fragment>
                                                            <React.Fragment>
                                                                {`${ renderItemValue(point) }`}
                                                            </React.Fragment>
                                                        </React.Fragment>
                                                }</div>
                                        ))
                                    }
                                </React.Fragment>
                            </React.Fragment>
                        ))}
                </React.Fragment>
            </div>
        );
    };


    const sharedTooltipRender = (context) => (<SharedTooltip {...context} />);

    const renderAxisCrossingValue = () => {
        let axisCrossingValue = [];

        if (!isEmpty(scores)) {
            if (!scores[0].axis) {
                return [];
            }
        }

        scores.map(item =>
            axisCrossingValue.push(new Date(moment(dashboardEndTimeStamp).add(1, 'days')))
        );

        axisCrossingValue.pop();
        axisCrossingValue.push(0);

        return axisCrossingValue.reverse();
    };

    const renderChartTitle = () => {
        if (selectedEquipment.name && selectedEquipmentRule.ruleName) {
            return `${selectedEquipment.name} - ${selectedEquipmentRule.ruleName} - Sensors`;
        }
        return 'Sensors';
    };

    if (!isActive) return null;

    return (
        <div className={classes.ScoresPanel}>
            <div style={{position: 'absolute',width: 0,height: 0}}>
                <svg xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <defs>
                        <pattern
                            id="pattern2" width="3" height="3"
                            patternTransform="rotate(45 0 0)" patternUnits="userSpaceOnUse"
                        >
                            <line
                                x1="0" y1="0" x2="0"
                                y2="10" style={{stroke: '#8d8d8d',strokeWidth: 1}}
                            />
                        </pattern>
                    </defs>
                </svg>
            </div>
            <div className={classes.TableContainer}>
                <Chart
                    style={{
                        height: scoreChartHeight
                    }}
                    ref = {chart}
                    pannable={{ lock: 'y' }}
                    zoomable={{ mousewheel: { lock: 'y' }, selection: { lock: 'y' } }}
                >
                    <ChartTitle text={renderChartTitle()} align="left" font="12pt sans-serif" />
                    <ChartTooltip
                        shared={true} visible = {true} render={sharedTooltipRender}
                        background="#FFFFFF"
                        opacity={.95}
                    />
                    {
                        !isEmpty(ganttDs) ?
                            <ChartPanes>
                                <ChartPane name="top" />
                                <ChartPane name="bottom" />
                            </ChartPanes>
                            :
                            <ChartPanes>
                                <ChartPane name="top" />
                            </ChartPanes>
                    }
                    {
                        !isEmpty(ganttDs) ?
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem
                                    baseUnit="minutes"
                                    baseUnitStep={15}
                                    pane="top"
                                    maxDivisions={12}
                                    labels={{ visible: isEmpty(ganttDs) ? true : false }}
                                    axisCrossingValue={renderAxisCrossingValue()}
                                />
                                <ChartCategoryAxisItem
                                    baseUnit="minutes"
                                    baseUnitStep={15}
                                    pane="bottom"
                                    maxDivisions={12}
                                />
                            </ChartCategoryAxis>
                            :
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem
                                    baseUnit="minutes"
                                    baseUnitStep={15}
                                    pane="top"
                                    maxDivisions={12}
                                    labels={{ visible: isEmpty(ganttDs) ? true : false }}
                                    axisCrossingValue={renderAxisCrossingValue()}
                                />
                            </ChartCategoryAxis>
                    }
                    <ChartLegend position="top" orientation="horizontal"  visible={true} />
                    <ChartSeries>
                        {scores.map((item, index) => (
                            <ChartSeriesItem
                                axis={item.axis}
                                markers={{ visible: false }}
                                type="line"
                                categoryField="timestamp"
                                name={item.name}
                                key={index}
                                data={item.data}
                                field="value"
                                unit={item.unit}
                                isWeather={item.isWeather}
                                // eslint-disable-next-line react/style-prop-object
                                style="smooth"
                            />
                        ))}
                        {ganttDs.map((item, i) => (
                            <ChartSeriesItem
                                axis={'bottom'}
                                markers={{ visible: (item.dataColor === '#ff6d01' || item.dataColor === '#6f6f6f') ?true:false, size: 4, background: `${item.dataColor}`}}
                                type="line"
                                width={6}
                                categoryField="timestamp"
                                name={item.sensorName}
                                key={`${item.sensorId}-${i}`}
                                visibleInLegend={false}
                                color={item.dataColor}
                                data={item.data}
                                field="value"
                            />
                        ))}
                    </ChartSeries>
                    <ChartValueAxis>
                        <ChartValueAxisItem
                            key={0}
                            name={'Left Y Axis'}
                            title={{ text: 'Sensors' }}
                            narrowRange={true}
                            majorGridLines={
                                { width: 1, dashType: 'dot' }
                            }
                        />
                        <ChartValueAxisItem
                            key={999}
                            name="bottom"
                            pane="bottom"
                            line={{ visible: true }}
                            majorGridLines={
                                { width: 1, dashType: 'dot' }
                            }
                            min={ganttAxisMin}
                            majorUnit={1}
                            max={ganttAxisMax}
                            labels={{ content: labelContent, visible: true }}
                        />
                    </ChartValueAxis>
                </Chart>
                {
                    bobLoading.includes('ScoreData') || bobLoading.includes('SensorIds') || bobLoading.includes('RuleKpi') ?
                        <BobLoadingMask />
                        :
                        null
                }
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        allSensors:            state.bob.allSensors,
        bobLoading:            state.bob.bobLoading,
        dashboardEndTimeStamp: state.dashboard.dashboardEndTimeStamp,
        entityScoringData:     state.bob.entityScoringData,
        entityScoresData:      state.bob.entityScoresData,
        rawUnits:              state.architect.rawUnits,
        selectedRuleSensors:   state.dashboard.selectedRuleSensors
    };
};

export default connect(mapStateToProps, null)(SensorGraph);
